import styled from "styled-components";
import { Box } from "@mui/material";

const INK = "#14202b";
const BLUE_DEEP = "#2f4b63";
const BLUE_MID = "#4a7092";
const PEACH = "#fdad73";
const PEACH_DEEP = "#fd944d";
const BLUSH = "#FFE3E3";
const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

export const FeaturesContainer = styled(Box)`
	padding: 88px 0 0;
	font-family: var(--poppins), "Poppins", sans-serif;
	color: ${INK};
	overflow-x: clip;

	h1,
	h2,
	h3,
	p {
		margin: 0;
	}

	.grid {
		margin-top: 40px;
		display: grid;
		grid-template-columns: repeat(12, minmax(0, 1fr));
		gap: 20px;
	}

	.cell {
		display: flex;
		> div {
			display: flex;
			width: 100%;
		}
		grid-column: span 4;
		&:nth-child(1) {
			grid-column: span 7;
		}
		&:nth-child(2) {
			grid-column: span 5;
		}
		&:nth-child(6) {
			grid-column: span 5;
		}
		&:nth-child(7) {
			grid-column: span 7;
		}
		&:nth-child(11),
		&:nth-child(12) {
			grid-column: span 6;
		}
	}

	.feature {
		position: relative;
		overflow: hidden;
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 14px;
		padding: 32px;
		border-radius: 32px;
		background: #fff;
		box-shadow: inset 0 0 0 1px rgba(120, 157, 188, 0.24);
		transition:
			transform 0.45s ${EASE},
			box-shadow 0.45s ${EASE};

		.icon-wrap {
			display: grid;
			place-items: center;
			width: 56px;
			height: 56px;
			border-radius: 50%;
			color: ${INK};
			background: linear-gradient(135deg, #ffc49b, ${PEACH} 55%, ${PEACH_DEEP});
			box-shadow: 0 10px 22px rgba(253, 148, 77, 0.3);
			transition: transform 0.5s ${EASE};
			svg {
				font-size: 26px;
			}
		}
		h3 {
			margin-top: 10px;
			font-family: inherit;
			font-size: clamp(20px, 2vw, 24px);
			font-weight: 700;
			letter-spacing: -0.02em;
			line-height: 1.25;
		}
		p {
			font-size: 15.5px;
			line-height: 1.7;
			color: ${({ theme }) => theme.palette.text.secondary};
			max-width: 52ch;
		}

		&:hover {
			transform: translateY(-5px);
			box-shadow:
				inset 0 0 0 1px rgba(253, 148, 77, 0.45),
				0 28px 52px rgba(47, 75, 99, 0.12);
			.icon-wrap {
				transform: rotate(-8deg) scale(1.06);
			}
		}

		/* Tinted variants give the grid rhythm without adding a second accent. */
		&.blush {
			background: ${BLUSH};
			box-shadow: inset 0 0 0 1px rgba(253, 148, 77, 0.2);
			p {
				color: #4a4a4a;
			}
		}
		&.deep {
			color: #fff;
			background:
				radial-gradient(60% 60% at 100% 100%, rgba(253, 173, 115, 0.35), transparent 70%),
				linear-gradient(150deg, ${BLUE_DEEP}, ${BLUE_MID});
			box-shadow: none;
			p {
				color: rgba(255, 255, 255, 0.86);
			}
		}
	}

	${props => props.theme.breakpoints.down("md")} {
		padding-top: 80px;
		.grid {
			margin-top: 40px;
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: 14px;
		}
		.cell,
		.cell:nth-child(n) {
			grid-column: span 1;
		}
		.cell:nth-child(1),
		.cell:nth-child(7) {
			grid-column: span 2;
		}
		.feature {
			padding: 24px;
			border-radius: 24px;
		}
	}

	${props => props.theme.breakpoints.down("sm")} {
		.grid {
			grid-template-columns: minmax(0, 1fr);
		}
		.cell:nth-child(n) {
			grid-column: span 1;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.feature,
		.feature .icon-wrap {
			transition: none;
		}
		.feature:hover,
		.feature:hover .icon-wrap {
			transform: none;
		}
	}
`;
